import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCircle,
	faChevronCircleRight,
} from "@fortawesome/pro-light-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";

import { API_URL_DONATE, fmtCurrency } from "../../utils/helpers";

import Alert from "../../components/Alert";
import Msg from "../../components/Msg";
import Submit from "../../components/Submit";

import ApplicantFields from "./ApplicantFields";
import BirthdayFields from "./BirthdayFields";
import AddressFields from "./AddressFields";
import SizesFields from "./SizesFields";
import EmergencyFields from "./EmergencyFields";
import OrderTable from "./OrderTable";

import "./styles.css";

export default function Details({ cares, id, handleStatus }) {
	const [alert, setAlert] = useState({
		type: "working",
		text: "",
	});
	const [data, setData] = useState([]);
	const [details, setDetails] = useState({});
	const [names, setNames] = useState({});
	const [errors, setErrors] = useState([]);
	const [msg, setMsg] = useState({});

	const formElement = useRef();

	const initRef = useRef(false);

	useEffect(() => {
		if (initRef.current) return;
		initRef.current = true;

		init();
	});

	const init = async () => {
		const url = new URL(`${API_URL_DONATE}/`);
		const params = {
			f: `ccDetailsInit`,
			id,
			x: uuidv4(),
		};
		url.search = new URLSearchParams(params);

		try {
			const response = await fetch(url.href, {
				method: "GET",
				cache: "no-store",
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				setData(json.data);
				setDetails({ ...json.details });
				handleUpdate("level", json.details.level);
				handleUpdate("dress", json.details.dress);
				setAlert({});
			}

			/*	maybe check that id === json.id and not 0
					if json.id === 0 then need to reset state id to 0
			*/
		} catch (error) {
			setAlert({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	const handleUpdate = (name, value) => {
		setNames((names) => ({ ...names, [name]: value ? value : "" }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors([]);
		setMsg({
			type: "working",
			text: "",
		});

		const url = new URL(`${API_URL_DONATE}/?f=ccDetails`);

		let formData = new FormData(formElement.current);
		// Object.entries(names).forEach(([key, value]) => formData.append(key, value));
		formData.append("level", names.level ? names.level : "");
		formData.append("id", id);

		try {
			const response = await fetch(url, {
				method: "POST",
				body: formData,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				setMsg({});
				handleStatus(json.id, 3);
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	return (
		<>
			{alert.type ? (
				<Alert data={alert} />
			) : (
				<form
					method="post"
					action="/"
					onSubmit={(e) => handleSubmit(e)}
					ref={formElement}
					id="coutoureclub"
				>
					<h2>2026-2027 Membership Registration</h2>

					<p style={{ marginTop: "15px", fontSize: "115%" }}>
						Please complete your details on form below to sign up for CouTOURe
						Club Membership. Deadline to register is October 31, 2026. If you
						need any assistance or have questions about registering, please
						contact{" "}
						<a href="mailto:Coutoureclub@gmail.com">coutoureclub@gmail.com</a>.
					</p>

					<p
						className="line"
						style={{ marginTop: "0", marginBottom: "15px" }}
					/>

					<h3>Select Membership Level</h3>

					<div className="buttons-logo-container">
						<div>
							<div className="buttons-container">
								{data.map((item) => {
									return (
										<button
											type="button"
											onClick={() => handleUpdate("level", item.id)}
											key={item.id}
										>
											<div>
												<FontAwesomeIcon
													icon={
														names.level === item.id ? faCircleCheck : faCircle
													}
													size="lg"
												/>
												<span>{fmtName(data, item.id)}</span>
											</div>
										</button>
									);
								})}
							</div>
						</div>
						<img
							src="https://childrenshealthcarecharity.org/wp-content/uploads/coutoure-club/cc-logo.png"
							alt="CouTOURe Club Logo"
						/>
					</div>

					<p
						className="line"
						style={{ marginTop: "0", marginBottom: "15px" }}
					/>
					<h3>Applicant Details</h3>
					<ApplicantFields
						data={{
							fname: details.fname,
							lname: details.lname,
							email: details.email,
							phone: details.phone,
						}}
						errors={errors}
					/>

					<p className="line" style={{ marginBottom: "15px" }} />
					<h3>Birth Date</h3>
					<BirthdayFields
						data={{
							month: details.month,
							day: details.day,
							year: details.year,
						}}
						errors={errors}
					/>

					<p className="line" style={{ marginBottom: "15px" }} />
					<h3>Address</h3>
					<AddressFields
						data={{
							address1: details.address1,
							address2: details.address2,
							city: details.city,
							st: details.st,
							zip: details.zip,
						}}
						errors={errors}
					/>

					<p className="line" style={{ marginBottom: "15px" }} />
					<h3>Clothing Sizes</h3>
					<SizesFields
						handleUpdate={handleUpdate}
						dress={names.dress ? names.dress : ""}
						data={{
							polo: details.polo,
							dress: details.dress,
							dressOther: details.dressOther,
						}}
						errors={errors}
					/>

					<p className="line" style={{ marginBottom: "15px" }} />
					<h3>Emergency Contact</h3>
					<EmergencyFields
						data={{
							emergencyFname: details.emergencyFname,
							emergencyLname: details.emergencyLname,
							emergencyEmail: details.emergencyEmail,
							emergencyPhone: details.emergencyPhone,
						}}
						errors={errors}
					/>

					<p className="line" style={{ marginBottom: "15px" }} />
					<h3>Order Summary</h3>

					<OrderTable data={data.filter((item) => item.id === names.level)} />

					<div className="msg-submit">
						{msg.type && <Msg data={msg} />}

						<div
							className={msg.type === "working" ? "hidden" : "submit-container"}
						>
							<Submit
								name="Continue to Photo Upload"
								icon={faChevronCircleRight}
								cares={cares}
							/>
						</div>
					</div>
					<p>
						We will not share your name or email address with third-parties.
					</p>
					<p className="spacer" />
				</form>
			)}
		</>
	);
}

function fmtName(data, id) {
	let str;
	data.forEach((item) => {
		if (item.id === id) {
			str = `${item.name} (${fmtCurrency(item.amount / 100)})`;
		}
	});

	return str;
}
